import * as React from 'react';
import { useContext, useMemo } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import { useTranslation } from 'react-i18next';

import { FareRulesQuery, useFareRulesQuery } from '@websky/graphql';
import { Mode, Locale } from '../../types';
import { ModeContext } from '../../context';
import { useTheme } from '../../theme';
import { useIsMobile } from '../../utils';
import { default as Button } from '../../Button';
import FareConditions from './FareConditions/FareConditions';
import ModalX from '../../BaseComponents/ModalX';

interface FareRulesBySegmentsPassengersMap {
	[segmentId: string]: FareRulesQuery['FareRules']['rulesForSegments'];
}

export interface Props {
	flightId: string;
	orderId?: string;
	isOpen: boolean;
	language: string;
	showTranslateTab?: boolean;
	onLangChange: (lang: Locale) => void;
	onClose: React.DispatchWithoutAction;
	segmentId?: string;
}

const FareConditionsModal: React.FC<Props> = React.memo(
	({ orderId, flightId, language, segmentId, showTranslateTab, onLangChange, onClose, isOpen }) => {
		const { t } = useTranslation('FareConditions');
		const { FareConditions: css } = useTheme('FareConditions');
		const mode = useContext(ModeContext);

		const [selectedLang, selectLang] = React.useState(Locale.Russian);
		const [segment, setSegment] = React.useState(segmentId);

		const isMobile = useIsMobile();
		const modalSize = isMobile ? 'fullscreen' : 'xlarge';

		const { data, loading, error, refetch } = useFareRulesQuery({
			variables: { flightId: flightId ?? orderId, orderId, language },
			skip: mode !== Mode.Booking || !isOpen
		});

		const hasErrors = error || (!loading && !data?.FareRules);

		const { fareRules, segmentTabs } = useMemo(() => {
			if (loading || hasErrors || !data?.FareRules?.rulesForSegments?.length) {
				return { fareRules: {}, segmentTabs: [] };
			}

			const fareRulesMap: FareRulesBySegmentsPassengersMap = {};
			const tabsSegmentsMap = new Map<string, { id: string; title: string; isDisabled: boolean }>();

			data.FareRules.rulesForSegments.forEach(ruleForSegment => {
				ruleForSegment.segments.forEach(segment => {
					const segmentId = segment.id;

					if (!fareRulesMap[segmentId]) {
						fareRulesMap[segmentId] = [];
					}

					fareRulesMap[segmentId].push(ruleForSegment);

					if (!tabsSegmentsMap.has(segmentId)) {
						const departureCityName = segment.departure.airport?.city?.name;
						const arrivalCityName = segment.arrival.airport?.city?.name;

						tabsSegmentsMap.set(segmentId, {
							id: segmentId,
							title: `${departureCityName} — ${arrivalCityName}`,
							isDisabled: false
						});
					}
				});
			});

			return {
				fareRules: fareRulesMap,
				segmentTabs: [...tabsSegmentsMap.values()]
			};
		}, [data, hasErrors, loading]);

		React.useEffect(() => {
			if (segmentTabs.length) {
				setSegment(segmentTabs[0].id);
			}
		}, [segmentTabs]);

		return (
			<ModalX isOpen={isOpen} onClose={onClose} size={modalSize} scroll="body" noPadding>
				{loading && (
					<div className={css.loaderWrp}>
						<CircularProgress classes={{ colorPrimary: css.loader }} />
					</div>
				)}

				{!loading && hasErrors && (
					<div className={css.errorWrp}>
						<h6 className={css.errorTitle}>{t('Internal error')}</h6>
						{error && <p className={css.errorText}>{error.message}</p>}

						<Button size="small" color="primary" onClick={() => refetch({ flightId, language })}>
							{t('Try again')}
						</Button>
					</div>
				)}

				{!loading && !hasErrors && (
					<FareConditions
						fareRulesBySegmentsPassengersMap={fareRules}
						fareRules={data}
						segment={segment}
						setSegment={setSegment}
						onLangChange={onLangChange}
						selectedLang={selectedLang}
						selectLang={selectLang}
						tabsSegments={segmentTabs}
						showTranslateTab={showTranslateTab}
					/>
				)}
			</ModalX>
		);
	}
);

export default FareConditionsModal;
